import React, { useState, useMemo } from "react";
import { CATALOGO_PARAMETROS, ParametroDefinicao } from "./enums";
import {
  TableContainer,
  Box,
  Typography,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Paper,
  Grid,
  TextField,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import { set } from "date-fns";

interface ConfiguracaoExtratorParametro {
  ConfiguracaoExtratorParametroId?: string;
  TipoParametro: number | null;
  NomeAtributo: string;
  DefinicaoAtributo?: string;
}

interface ModalAdicionarParametroProps {
  listaAtual: ConfiguracaoExtratorParametro[];
  onSalvar: (novoParametro: ConfiguracaoExtratorParametro) => void;
  onCancelar: () => void;
}

export const ModalAdicionarParametro: React.FC<
  ModalAdicionarParametroProps
> = ({ listaAtual, onSalvar, onCancelar }): React.ReactElement => {
  // Estado para o item selecionado no Dropdown
  const [selectedId, setSelectedId] = useState<string>("");

  const [formData, setFormData] = useState<
    Partial<ConfiguracaoExtratorParametro>
  >({
    NomeAtributo: "",
    DefinicaoAtributo: "",
  });

  // Lógica de Filtragem: Remove do catálogo o que já existe na listaAtual
  const opcoesDisponiveis = useMemo(() => {
    return CATALOGO_PARAMETROS.filter((opcaoCatalogo) => {
      // Verifica se existe algum item na lista atual com o mesmo TipoParametro
      const jaExiste = listaAtual.some(
        (itemAtual) => itemAtual.TipoParametro === opcaoCatalogo.id
      );
      return !jaExiste;
    }).sort((a, b) => a.label.localeCompare(b.label));
  }, [listaAtual]);

  // Handler de mudança do Select
  const handleSelectChange = (event: any, newValue: any) => {
    if (!newValue) {
      setSelectedId("");
      setFormData({
        NomeAtributo: "",
        DefinicaoAtributo: "",
      });
      return;
    }

    setSelectedId(String(newValue.id));
    setFormData({
      TipoParametro: newValue.id,
      NomeAtributo: newValue.defaultName,
      DefinicaoAtributo: newValue.defaultDefinition || "",
    });
  };

  const handleSubmit = () => {
    if (!selectedId && selectedId !== "null") return;

    const novoItem: ConfiguracaoExtratorParametro = {
      ConfiguracaoExtratorParametroId: undefined,
      TipoParametro: formData.TipoParametro!,
      NomeAtributo: formData.NomeAtributo!,
      DefinicaoAtributo: formData.DefinicaoAtributo,
    };

    onSalvar(novoItem);
  };

  const valorAtualAutocomplete =
    opcoesDisponiveis.find((opcao) => String(opcao.id) === selectedId) || null;

  return (
    <Paper
      elevation={0}
      style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}
    >
      <Box mb={3} borderBottom="1px solid var(--border-default)" pb={1}>
        <Typography variant="h6" color="primary">
          Adicionar Novo Parâmetro
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Selecione o tipo de parâmetro para preencher automaticamente as
          configurações.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* COLUNA 1: AUTOCOMPLETE COM BUSCA */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={opcoesDisponiveis}
            getOptionLabel={(option) => option.label}
            value={valorAtualAutocomplete}
            onChange={handleSelectChange}
            disabled={opcoesDisponiveis.length === 0}
            noOptionsText="Nenhum parâmetro encontrado"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo de Parâmetro"
                variant="outlined"
                placeholder="Ex: Faturamento..."
                error={opcoesDisponiveis.length === 0}
                helperText={
                  opcoesDisponiveis.length === 0
                    ? "Todos os parâmetros disponíveis já foram adicionados."
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* COLUNA 2: NOME (READONLY) */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Nome do Atributo (Snake Case)"
            value={formData.NomeAtributo}
            fullWidth
            variant="outlined"
            InputProps={{
              readOnly: true,
              style: { backgroundColor: "var(--surface-sunken)", color: "var(--text-secondary)" }, // Visual de desabilitado
            }}
            helperText="O nome é preenchido automaticamente com base no tipo."
          />
        </Grid>

        {/* LINHA INTEIRA: DEFINIÇÃO/PROMPT */}
        <Grid item xs={12}>
          <TextField
            label="Definição / Prompt (Opcional)"
            value={formData.DefinicaoAtributo}
            onChange={(e) =>
              setFormData({
                ...formData,
                DefinicaoAtributo: e.target.value,
              })
            }
            fullWidth
            multiline
            minRows={3}
            variant="outlined"
            placeholder="Descreva regras específicas para a IA extrair este campo..."
          />
        </Grid>
      </Grid>

      {/* BOTÕES DE AÇÃO */}
      <Box display="flex" justifyContent="flex-end" mt={4} gap={2}>
        <Button variant="outlined" color="error" onClick={onCancelar}>
          Cancelar
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!selectedId}
          size="large"
        >
          Adicionar
        </Button>
      </Box>
    </Paper>
  );
};
